import { AUTH_REFRESH_TOKEN_ROTATE_ON, readSdkRefreshToken, readSdkUserIdentity } from './auth0Session.helpers';
import { toBase64, wrap } from './pinCrypto.helpers';
import { getCachedPinKey, setCachedPinKey } from './pinKeyCache.helpers';

// The PIN wrap key outlives a page reload for as long as the Auth0 session does, so a rotated refresh
// token can be re-wrapped without the PIN. It sits beside the SDK's refresh token and is removed with it.

export const PIN_KEY_PENDING_EVENT = 'savi:pin-key-pending';

const DB_NAME = 'savi-pin-session';
const STORE = 'wrapKey';
const RECORD_ID = 'current';
const LOCK_NAME = 'savi-pin-session-key';

// Serialises save, rewrap and clear across tabs, so a slow rewrap can't restore a cleared key.
const withLock = <T>(task: () => Promise<T>): Promise<T> =>
  typeof navigator !== 'undefined' && navigator.locks ? navigator.locks.request(LOCK_NAME, task) : task();

type SessionKeyRecord = {
  orgId: string;
  email: string;
  key: CryptoKey;
  /** Salt of the cached pin.key that `key` was derived with. */
  salt: string;
  /** SHA-256 of the refresh token that the cached pin.key currently wraps. */
  refreshTokenHash: string;
};

const withStore = async <T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    const open = indexedDB.open(DB_NAME, 1);
    open.onupgradeneeded = () => open.result.createObjectStore(STORE);
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error);
  });
  try {
    return await new Promise<T>((resolve, reject) => {
      const request = run(db.transaction(STORE, mode).objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } finally {
    db.close();
  }
};

export const hashRefreshToken = async (refreshToken: string): Promise<string> =>
  toBase64(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(refreshToken))));

const loadSessionWrapKey = (): Promise<SessionKeyRecord | undefined> =>
  withStore('readonly', store => store.get(RECORD_ID));

const deleteRecord = async (): Promise<void> => {
  try {
    await withStore('readwrite', store => store.delete(RECORD_ID));
  } catch {
    // IndexedDB unavailable: there is no stored key to clear
  }
};

/** Best effort: on failure the session simply has no key, and any earlier one is dropped. */
export const saveSessionWrapKey = (
  orgId: string,
  email: string,
  key: CryptoKey,
  salt: string,
  refreshToken: string,
): Promise<void> =>
  withLock(async () => {
    try {
      const record: SessionKeyRecord = {
        orgId,
        email,
        key,
        salt,
        refreshTokenHash: await hashRefreshToken(refreshToken),
      };
      await withStore('readwrite', store => store.put(record, RECORD_ID));
    } catch {
      await deleteRecord();
    }
  });

export const clearSessionWrapKey = (): Promise<void> => withLock(deleteRecord);

let inFlight: Promise<void> | undefined;

const rewrapIfRotated = async (): Promise<void> => {
  if (!AUTH_REFRESH_TOKEN_ROTATE_ON) return;

  const session = await loadSessionWrapKey();
  if (!session) return;

  const refreshToken = readSdkRefreshToken();
  if (!refreshToken) return;

  const identity = readSdkUserIdentity();
  const sameUser =
    identity?.email?.toLowerCase() === session.email.toLowerCase() &&
    (!identity.orgId || identity.orgId === session.orgId);
  if (!sameUser) {
    await deleteRecord();
    return;
  }

  const hash = await hashRefreshToken(refreshToken);
  if (hash === session.refreshTokenHash) return;

  // a pin.key from another device or a removed PIN means this key no longer matches it
  const isCurrent = (entry: ReturnType<typeof getCachedPinKey>) =>
    !!entry && !entry.pendingDelete && entry.tokenStore.salt === session.salt;
  const entry = getCachedPinKey(session.orgId, session.email);
  if (!entry || !isCurrent(entry)) {
    await deleteRecord();
    return;
  }

  const { iv, ciphertext } = await wrap(session.key, refreshToken);
  const latest = getCachedPinKey(session.orgId, session.email);
  if (!latest || !isCurrent(latest)) {
    await deleteRecord();
    return;
  }
  setCachedPinKey(session.orgId, session.email, {
    ...latest,
    tokenStore: { salt: latest.tokenStore.salt, iv, ciphertext },
    pendingUpload: true,
  });
  await withStore('readwrite', store => store.put({ ...session, refreshTokenHash: hash }, RECORD_ID));
  window.dispatchEvent(new Event(PIN_KEY_PENDING_EVENT));
};

/** Re-wraps the SDK's refresh token into the cached pin.key if it has rotated since it was last wrapped. */
export const rewrapRefreshTokenIfRotated = (): Promise<void> => {
  inFlight ??= withLock(rewrapIfRotated)
    .catch(() => undefined)
    .finally(() => {
      inFlight = undefined;
    });
  return inFlight;
};

/**
 * Ends the session's key: catches up on a refresh that bypassed the interceptor (direct
 * getAccessTokenSilently calls), then drops the key.
 */
export const endSessionWrapKey = (): Promise<void> =>
  withLock(async () => {
    await rewrapIfRotated().catch(() => undefined);
    await deleteRecord();
  });
